import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from "@ng-select/ng-select";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ScrollingModule } from '@angular/cdk/scrolling';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { HomeLayoutComponent } from './layouts/home-layout/home-layout.component';

import { HeaderComponent } from './common/header/header.component';
import { FooterComponent } from './common/footer/footer.component';
import { SidebarComponent } from './common/sidebar/sidebar.component';
import { NotFoundComponent } from './common/not-found/not-found.component';

import { LoginComponent } from './components/login/login.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';

import { ApiService } from "./services/api.service";
import { AuthService } from "./services/auth.service";
import { AuthGuardService } from "./services/auth-guard.service";
import { BackOfficeComponent } from './components/back-office/back-office.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { TestComponent } from './components/test/test.component';
import { AccountSettingsComponent } from './components/back-office/account-settings/account-settings.component';
import { HotelSetupComponent } from './components/back-office/hotel-setup/hotel-setup.component';
import { CreateUsersComponent } from './components/back-office/create-users/create-users.component';
import { ManageUsersComponent } from './components/back-office/manage-users/manage-users.component';
import { CreateRoomsComponent } from './components/back-office/create-rooms/create-rooms.component';
import { ManageRoomsComponent } from './components/back-office/manage-rooms/manage-rooms.component';
import { ToastrModule } from 'ngx-toastr';
import { RequestInterceptor } from "./services/requestInterceptor";
import { ConfirmationAlertComponent } from './common/confirmation-alert/confirmation-alert.component';
import { SearchPipe } from './common/searchpipe';
import { RecursiveSearchPipe } from './common/recursivepipe';
import { SortPipe } from './common/sortpipe';
import { BillingComponent } from './components/back-office/billing/billing.component';
import { TermsOfUseComponent } from './components/back-office/terms-of-use/terms-of-use.component';
import { OnlineHelpComponent } from './components/back-office/online-help/online-help.component';
import { ManageOtasComponent } from './components/manage-otas/manage-otas.component';
import { InvoiceComponent } from './components/back-office/billing/invoice/invoice.component';
import { SubscriptionComponent } from './components/back-office/billing/subscription/subscription.component';
import { ManageHowYouPayComponent } from './components/back-office/billing/manage-how-you-pay/manage-how-you-pay.component';
import { PlanSwtichComponent } from './components/back-office/billing/plan-swtich/plan-swtich.component';
import { ContactSupportComponent } from './components/back-office/contact-support/contact-support.component';
import { SelectHotelComponent } from './components/back-office/select-hotel/select-hotel.component';
import { ManageRatesPackagesComponent } from './components/manage-rates-packages/manage-rates-packages.component';
import { Test2Component } from './components/test2/test2.component';

import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material';
import { MatInputModule } from '@angular/material';
import { MatNativeDateModule, MatRippleModule, DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';

import { ManageRatesPackagesService } from './components/manage-rates-packages/manage-rates-packages.service';
import { SetupPackageComponent } from './components/manage-rates-packages/setup-package/setup-package.component';
import { SetupRateComponent } from './components/manage-rates-packages/setup-rate/setup-rate.component';
import { SetupRestrictionComponent } from './components/manage-rates-packages/setup-restriction/setup-restriction.component';
import { LinkOTAComponent } from './components/manage-rates-packages/link-ota/link-ota.component';
import { PreviewPackageComponent } from './components/manage-rates-packages/preview-package/preview-package.component';
import { ArrivalsComponent } from './components/arrivals/arrivals.component';
import { WithoutHeaderComponent } from './layouts/without-header/without-header.component';
import { InventoryComponent } from './components/inventory/inventory.component';
import { ReportsComponent } from './components/reports/reports.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { VirtualScrollerModule } from 'ngx-virtual-scroller';
import { LogComponent } from './components/log/log.component';
import { HouseKeepingComponent } from './components/house-keeping/house-keeping.component';
import { TestkhiComponent } from './components/testkhi/testkhi.component';
import { MatChipsModule } from '@angular/material/chips';
import { CommunicationComponent } from './components/communication/communication.component';
import { EmailDirectoryComponent } from './components/communication/email-directory/email-directory.component';
import { NgxSummernoteModule } from 'node_modules/ngx-summernote';
import { EmailTemplateComponent } from './components/communication/email-template/email-template.component';
import { PreviewRoomsComponent } from './components/back-office/preview-rooms/preview-rooms.component';
import {MatIconModule} from '@angular/material';
import { VendorComponent } from './components/back-office/vendor/vendor.component';// this must add to app module.ts
import { NgxPaginationModule } from 'ngx-pagination';

import { NgxSpinnerModule } from "ngx-spinner";
import { RegistrationComponent } from './components/registration/registration.component';
import { PaynowInvoiceComponent } from './components/paynow-invoice/paynow-invoice.component';
import { PreviewHotelComponent } from './components/back-office/preview-hotel/preview-hotel.component';
import { Stays2Component } from './components/stays2/stays2.component';

export const DD_MM_YYYY_Format = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@NgModule({
  declarations: [
    AppComponent,
    HomeLayoutComponent,
    HeaderComponent,
    FooterComponent,
    NotFoundComponent,
    LoginComponent,
    DashboardComponent,
    SidebarComponent,
    ForgotPasswordComponent,
    BackOfficeComponent,
    ResetPasswordComponent,
    TestComponent,
    AccountSettingsComponent,
    HotelSetupComponent,
    CreateUsersComponent,
    ManageUsersComponent,
    CreateRoomsComponent,
    ManageRoomsComponent,
    ConfirmationAlertComponent,
    SearchPipe,
    SortPipe,
    RecursiveSearchPipe,
    BillingComponent,
    TermsOfUseComponent,
    OnlineHelpComponent,
    ManageOtasComponent,
    InvoiceComponent,
    SubscriptionComponent,
    ManageHowYouPayComponent,
    PlanSwtichComponent,
    ContactSupportComponent,
    SelectHotelComponent,
    ManageRatesPackagesComponent,
    Test2Component,
    SetupPackageComponent,
    SetupRateComponent,
    SetupRestrictionComponent,
    LinkOTAComponent,
    PreviewPackageComponent,
    ArrivalsComponent,
    WithoutHeaderComponent,
    InventoryComponent,
    ReportsComponent,
    LogComponent,
    HouseKeepingComponent,
    TestkhiComponent,
    CommunicationComponent,
    EmailDirectoryComponent,
    EmailTemplateComponent,
    PreviewRoomsComponent,
    VendorComponent,
    RegistrationComponent,
    PaynowInvoiceComponent,
    PreviewHotelComponent,
    Stays2Component,
  ],
  imports: [
    NgxSummernoteModule,
    BrowserModule,
    HttpClientModule,
    FormsModule,
    NgSelectModule,
    ReactiveFormsModule,
    NgSelectModule,
    MatChipsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatIconModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-center',
      preventDuplicates: true,
    }),
    NgxDaterangepickerMd.forRoot(),
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRippleModule,
    MatTableModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule.withConfig({ warnOnNgModelWithFormControl: 'never' }),
    ScrollingModule,
    VirtualScrollerModule,
    NgxPaginationModule,
    NgxSpinnerModule
  ],
  exports: [
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    FormsModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    ApiService,
    AuthService,
    ManageRatesPackagesService,
    AuthGuardService,
    HomeLayoutComponent,
    WithoutHeaderComponent,
    RequestInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true
    },
    SearchPipe,
    RecursiveSearchPipe,
    SortPipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_Format }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
